import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const RefinementPanel = ({ versionId, onMerged }) => {
  const { user } = useAuth();
  const [refinements, setRefinements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [newContent, setNewContent] = useState('');
  const [referenceText, setReferenceText] = useState('');
  const [selected, setSelected] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [editingContent, setEditingContent] = useState('');
  const [merging, setMerging] = useState(false);
  const requestIdRef = useRef(0);

  const loadRefinements = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setLoading(true);
    try {
      const { data } = await api.get(`/api/versions/${versionId}/refinements`);
      if (requestIdRef.current !== requestId) return;
      setRefinements(data || []);
    } catch {
      toast.error('Failed to load refinements');
    } finally {
      if (requestIdRef.current === requestId) {
        setLoading(false);
      }
    }
  }, [versionId]);

  useEffect(() => {
    loadRefinements();
  }, [loadRefinements]);

  const handleSubmit = async () => {
    const content = newContent.trim();
    if (!content) {
      toast.error('Enter refinement text');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/api/versions/${versionId}/refinements`, {
        content,
        reference_text: referenceText || null,
      });
      setNewContent('');
      setReferenceText('');
      toast.success('Refinement submitted');
      loadRefinements();
    } catch {
      toast.error('Failed to submit refinement');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/refinements/${id}`);
      toast.success('Refinement deleted');
      loadRefinements();
    } catch {
      toast.error('Failed to delete refinement');
    }
  };

  const handleSaveEdit = async () => {
    const content = editingContent.trim();
    if (!content || !editingId) return;
    try {
      await api.put(`/api/refinements/${editingId}`, { content });
      setEditingId(null);
      setEditingContent('');
      toast.success('Refinement updated');
      loadRefinements();
    } catch {
      toast.error('Failed to update refinement');
    }
  };

  const handleMergeSelected = async () => {
    const refinementIds = Object.keys(selected)
      .filter((id) => selected[id])
      .map((id) => Number(id));

    if (refinementIds.length === 0) {
      toast.error('Select at least one refinement');
      return;
    }

    setMerging(true);
    try {
      const { data } = await api.post(`/api/versions/${versionId}/merge-refinements`, {
        refinement_ids: refinementIds,
      });
      toast.success('Refinements merged into new version');
      onMerged?.(data);
    } catch {
      toast.error('Failed to merge refinements');
    } finally {
      setMerging(false);
    }
  };

  return (
    <section className="panel panel--spaced refinement-panel">
      <h3>Collaborative Refinements</h3>
      <p className="muted">Suggestions are visible to all collaborators. Only the author can edit/delete.</p>

      <div className="form-group">
        <label htmlFor="new-refinement">Add refinement</label>
        <textarea
          id="new-refinement"
          value={newContent}
          onChange={(e) => setNewContent(e.target.value)}
          placeholder="Suggest an improvement to this policy..."
        />
      </div>
      <div className="form-group">
        <label htmlFor="new-refinement-reference">Quote section (optional)</label>
        <textarea
          id="new-refinement-reference"
          value={referenceText}
          onChange={(e) => setReferenceText(e.target.value)}
          placeholder="Paste the section this suggestion refers to..."
        />
      </div>
      <button className="btn btn--primary" onClick={handleSubmit} disabled={submitting}>
        {submitting ? 'Submitting...' : 'Submit Refinement'}
      </button>

      <div className="refinement-panel__list">
        {loading && <p className="muted">Loading refinements...</p>}
        {!loading && refinements.length === 0 && <p className="muted">No refinements yet.</p>}
        {refinements.map((item) => {
          const isAuthor = item.author_email && user?.email && item.author_email === user.email;
          return (
            <article key={item.id} className="refinement-item">
              <div className="refinement-item__head">
                <label className="checkbox-inline">
                  <input
                    type="checkbox"
                    checked={!!selected[item.id]}
                    onChange={(e) => setSelected((prev) => ({ ...prev, [item.id]: e.target.checked }))}
                  />
                  Select
                </label>
                <span className="muted">{item.author_name || item.author_email || 'Unknown'} • {new Date(item.created_at).toLocaleString()}</span>
              </div>

              {editingId === item.id ? (
                <>
                  <textarea value={editingContent} onChange={(e) => setEditingContent(e.target.value)} />
                  <div className="row-actions">
                    <button className="btn btn--sm btn--primary" onClick={handleSaveEdit}>Save</button>
                    <button className="btn btn--sm" onClick={() => setEditingId(null)}>Cancel</button>
                  </div>
                </>
              ) : (
                <>
                  <p>{item.content}</p>
                  {item.reference_text && <p className="muted">Reference: {item.reference_text}</p>}
                </>
              )}

              <div className="row-actions">
                <span className={`status-pill status-pill--${item.status || 'pending'}`}>{item.status || 'pending'}</span>
                {isAuthor && editingId !== item.id && (
                  <button
                    className="btn btn--sm"
                    onClick={() => {
                      setEditingId(item.id);
                      setEditingContent(item.content || '');
                    }}
                  >
                    Edit
                  </button>
                )}
                {isAuthor && <button className="btn btn--sm" onClick={() => handleDelete(item.id)}>Delete</button>}
              </div>
            </article>
          );
        })}
      </div>

      <button className="btn btn--primary" onClick={handleMergeSelected} disabled={merging || loading}>
        {merging ? 'Merging...' : 'Merge Selected Refinements'}
      </button>
    </section>
  );
};

export default RefinementPanel;
